import { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { CriteriaGroup, Criterion, Importance, ScoreValue } from '@/types/evaluation.types';
import { ScoreRadioGroup } from './ScoreRadioGroup';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { Check, AlertCircle } from 'lucide-react';

interface ScorecardTableProps {
  criteriaGroups: CriteriaGroup[];
  scores: Record<string, ScoreValue>;
  onScoreChange: (criterionId: string, score: ScoreValue) => void;
  scrollToGroupId?: string | null;
  isLocked?: boolean;
}

/**
 * Dense table layout for efficient scoring of all criteria
 */
export function ScorecardTable({
  criteriaGroups,
  scores,
  onScoreChange,
  scrollToGroupId,
  isLocked = false,
}: ScorecardTableProps) {
  const { t } = useTranslation(['scorecard', 'common']);
  const groupRefs = useRef<Record<string, HTMLElement | null>>({});

  // Scroll to group when scrollToGroupId changes
  useEffect(() => {
    if (scrollToGroupId && groupRefs.current[scrollToGroupId]) {
      groupRefs.current[scrollToGroupId]?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [scrollToGroupId]);

  const getImportanceBadge = (importance: Importance) => {
    const config = {
      Critical: { variant: 'destructive' as const, label: t('common:importance.critical') },
      High: { variant: 'default' as const, label: t('common:importance.high') },
      Medium: { variant: 'secondary' as const, label: t('common:importance.medium') },
      Low: { variant: 'outline' as const, label: t('common:importance.low') },
      None: { variant: 'outline' as const, label: t('common:importance.none') },
    };

    const { variant, label } = config[importance];

    return (
      <Badge variant={variant} className="text-xs whitespace-nowrap">
        {label}
      </Badge>
    );
  };

  const groupCriteria = (criteria: Criterion[]) => {
    const grouped = new Map<string, Criterion[]>();

    criteria.forEach((criterion) => {
      const category = criterion.category || t('scorecard:table.noCategory');
      if (!grouped.has(category)) {
        grouped.set(category, []);
      }
      grouped.get(category)!.push(criterion);
    });

    return grouped;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="border rounded-lg overflow-hidden bg-background">
        {/* Table Header - Sticky - Hidden on mobile, shown on tablet+ */}
        <div className="sticky z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/90 border-b shadow-sm hidden md:block">
          <div className="grid md:grid-cols-[160px_100px_1fr_100px_380px] lg:grid-cols-[180px_120px_1fr_110px_400px] gap-3 lg:gap-4 px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            <div>{t('scorecard:table.group')}</div>
            <div>{t('scorecard:table.category')}</div>
            <div>{t('scorecard:table.criterion')}</div>
            <div>{t('scorecard:table.importance')}</div>
            <div>{t('scorecard:table.score')}</div>
          </div>
        </div>

        {/* Table Body */}
        <div className="divide-y">
          {criteriaGroups.map((group) => {
            const categorizedCriteria = groupCriteria(group.criteria);
            const groupScored = group.criteria.filter((c) => scores[c.id] !== null && scores[c.id] !== undefined).length;
            const groupTotal = group.criteria.length;

            return (
              <div
                key={group.id}
                data-testid={`scorecard-group-${group.id}`}
                ref={(el) => (groupRefs.current[group.id] = el)}
                className="scroll-mt-[188px]"
              >
                {/* Group Header - Sticky within section */}
                <div className="sticky z-[9] bg-primary/10 backdrop-blur border-b-2 border-primary/20 border-t-2 mt-4 first:mt-0">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 px-4 py-3">
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                      <h3 className="text-sm font-semibold">{group.name}</h3>
                      <Badge variant="outline" className="text-xs">
                        {t('common:label.weight')}: {group.weight}%
                      </Badge>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <span>
                          {groupScored}/{groupTotal} {t('scorecard:table.scored')}
                        </span>
                        {groupScored === groupTotal && groupTotal > 0 && (
                          <Check className="h-3 w-3 text-green-600" aria-hidden="true" />
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Criteria Rows */}
                {Array.from(categorizedCriteria.entries()).map(([category, criteria]) => (
                  <div key={`${group.id}-${category}`}>
                    {/* Category subheader (only if multiple categories) */}
                    {categorizedCriteria.size > 1 && (
                      <div className="bg-muted/40 px-4 py-2 border-b">
                        <span className="text-xs font-medium text-muted-foreground">
                          {category}
                        </span>
                      </div>
                    )}

                    {/* Criterion rows */}
                    {criteria.map((criterion, index) => {
                      const score = scores[criterion.id];
                      const isScored = score !== null && score !== undefined;

                      return (
                        <div
                          key={criterion.id}
                          data-testid={`scorecard-criterion-${criterion.id}`}
                          className={cn(
                            'px-4 py-3 hover:bg-muted/50 transition-colors',
                            isScored ? 'bg-green-50/30 dark:bg-green-950/10' : ''
                          )}
                        >
                          {/* Mobile layout: Stacked */}
                          <div className="md:hidden space-y-3">
                            {/* Mobile: Criterion content */}
                            <div className="space-y-2">
                              <div className="flex items-start gap-2">
                                {!isScored && (
                                  <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
                                )}
                                {isScored && (
                                  <Check className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium break-words">{criterion.title}</p>
                                  {criterion.description && (
                                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed break-words">
                                      {criterion.description}
                                    </p>
                                  )}
                                  {criterion.helpfulHints && (
                                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1 italic break-words">
                                      💡 {criterion.helpfulHints}
                                    </p>
                                  )}
                                </div>
                              </div>

                              {/* Mobile: Category and Importance */}
                              <div className="flex flex-wrap items-center gap-2 ml-6">
                                {categorizedCriteria.size > 1 && (
                                  <span className="text-xs text-muted-foreground">{category}</span>
                                )}
                                {getImportanceBadge(criterion.importance)}
                              </div>
                            </div>

                            {/* Mobile: Score selector */}
                            <div className={cn("ml-6", isLocked && "opacity-60 pointer-events-none")}>
                              <ScoreRadioGroup
                                value={score}
                                onChange={(newScore) => onScoreChange(criterion.id, newScore)}
                                criterionId={criterion.id}
                              />
                            </div>
                          </div>

                          {/* Tablet/Desktop layout: Grid */}
                          <div className="hidden md:grid md:grid-cols-[160px_100px_1fr_100px_380px] lg:grid-cols-[180px_120px_1fr_110px_400px] gap-3 lg:gap-4">
                            {/* Group name (only show for first criterion in category) */}
                            <div className="text-sm text-muted-foreground truncate">
                              {index === 0 && categorizedCriteria.size === 1 ? group.name : ''}
                            </div>

                            {/* Category */}
                            <div className="text-sm text-muted-foreground truncate">
                              {categorizedCriteria.size === 1 ? category : ''}
                            </div>

                            {/* Criterion */}
                            <div className="space-y-1 min-w-0">
                              <div className="flex items-start gap-2">
                                {!isScored && (
                                  <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
                                )}
                                {isScored && (
                                  <Check className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium break-words">{criterion.title}</p>
                                  {criterion.description && (
                                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed break-words">
                                      {criterion.description}
                                    </p>
                                  )}
                                  {criterion.helpfulHints && (
                                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1 italic break-words">
                                      💡 {criterion.helpfulHints}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Importance */}
                            <div className="flex items-start pt-1">
                              {getImportanceBadge(criterion.importance)}
                            </div>

                            {/* Score */}
                            <div className={cn("flex items-start pt-1", isLocked && "opacity-60 pointer-events-none")}>
                              <ScoreRadioGroup
                                value={score}
                                onChange={(newScore) => onScoreChange(criterion.id, newScore)}
                                criterionId={criterion.id}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
